'use client';

import { useState } from 'react';
import { BlogCategoryDocument } from '../../../../../prismicio-types';
import { CheckBox } from './Controls';
import { CONTENT_TYPES } from './types';

const DEFAULT_PANEL_CLASS =
  'rounded-2xl border border-[#ccc] bg-white p-4 backdrop-blur-[21px]';
const DEFAULT_ITEM_LABEL_CLASS = 'text-sm font-medium text-black';

export function FilterSidebar({
  categories,
  activeTypes,
  activeCategories,
  onToggleType,
  onToggleCategory,
  onClearAll,
  className,
  panelClassName = DEFAULT_PANEL_CLASS,
  typeLabelClassName = DEFAULT_ITEM_LABEL_CLASS,
  topicLabelClassName = DEFAULT_ITEM_LABEL_CLASS,
}: {
  categories: BlogCategoryDocument[];
  activeTypes: Set<string>;
  activeCategories: Set<string>;
  onToggleType: (type: string) => void;
  onToggleCategory: (uid: string) => void;
  onClearAll: () => void;
  className?: string;
  /** Panel chrome (border, radius, padding, background). */
  panelClassName?: string;
  /** Type-section option labels. */
  typeLabelClassName?: string;
  /** Topic-section option labels. */
  topicLabelClassName?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasFilters = activeTypes.size > 0 || activeCategories.size > 0;

  return (
    <>
      {/* Mobile toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-2xl border border-[#ccc] bg-white px-4 py-3 text-left font-bold lg:hidden"
      >
        <span>
          Filter{' '}
          {hasFilters && (
            <span className="text-sm font-normal text-gray-500">
              ({activeTypes.size + activeCategories.size} active)
            </span>
          )}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {/* Sidebar panel */}
      <div
        className={`w-full shrink-0 lg:block lg:w-[220px] ${
          isOpen ? 'block' : 'hidden'
        } ${className ?? ''}`}
      >
        <div className={panelClassName}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold text-black">Filter</span>
            {hasFilters && (
              <button
                type="button"
                onClick={onClearAll}
                className="text-sm font-semibold text-[#4b0fea] underline"
              >
                Clear
              </button>
            )}
          </div>

          <hr className="my-4 border-[#ccc]" />

          {/* Type section */}
          <div className="flex flex-col gap-2 border-b border-[#ccc] pb-4">
            <p className="text-base font-semibold text-black">Type</p>
            <div className="flex flex-col gap-3">
              {CONTENT_TYPES.map((ct) => (
                <label
                  key={ct.id}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <CheckBox
                    checked={activeTypes.has(ct.id)}
                    onChange={() => onToggleType(ct.id)}
                  />
                  <span className={typeLabelClassName}>{ct.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Topic section */}
          <div className="mt-4 flex flex-col gap-2">
            <p className="text-base font-semibold text-black">Topic</p>
            <div className="flex max-h-[400px] flex-col gap-3 overflow-y-auto">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <CheckBox
                    checked={activeCategories.has(cat.uid!)}
                    onChange={() => onToggleCategory(cat.uid!)}
                  />
                  <span className={topicLabelClassName}>
                    {cat.data?.name || cat.uid}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
